import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, Download, CheckCircle } from 'lucide-react';
import AssessmentLayout from '../../components/assessment/AssessmentLayout';
import { useAuth } from '../../contexts/AuthContext';

interface ReportData {
  inmateName: string;
  contactPerson: string;
  contactPersonRole: string;
  assessmentDate: string;
  recommendedModule: string;
  moduleRiskLevel: string;
}

export default function AssessmentReport() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [reportData, setReportData] = useState<ReportData>({
    inmateName: '',
    contactPerson: '',
    contactPersonRole: '',
    assessmentDate: new Date().toLocaleDateString('nb-NO'),
    recommendedModule: '',
    moduleRiskLevel: ''
  });

  // Get the user's display name
  const completedByName = profile?.fullName || user?.email || 'Anonym';

  useEffect(() => {
    // Get the module recommendation from sessionStorage
    const moduleRecommendation = sessionStorage.getItem('moduleRecommendation');
    
    if (moduleRecommendation) {
      try {
        const recommendation = JSON.parse(moduleRecommendation);
        
        setReportData(prev => ({
          ...prev,
          recommendedModule: recommendation.module || 'Modul 3',
          moduleRiskLevel: recommendation.riskLevel || 'low'
        }));
      } catch (error) {
        console.error('Error parsing module recommendation:', error);
        // Fallback to default values
        setReportData(prev => ({
          ...prev,
          recommendedModule: 'Modul 3',
          moduleRiskLevel: 'low'
        }));
      }
    } else {
      // If no module recommendation, check for assessment data as fallback
      const assessmentDataStr = sessionStorage.getItem('assessmentData');
      
      if (assessmentDataStr) {
        try {
          const data = JSON.parse(assessmentDataStr);
          
          // Analyze symptoms to determine module
          let highSeverityCount = 0;
          let moderateSeverityCount = 0;
          
          data.forEach((category: any) => {
            category.symptoms?.forEach((symptom: any) => {
              if (symptom.value >= 75) highSeverityCount++;
              else if (symptom.value >= 50) moderateSeverityCount++;
            });
          });
          
          let module = 'Modul 3';
          let riskLevel = 'low';
          
          if (highSeverityCount >= 5 || (highSeverityCount >= 3 && moderateSeverityCount >= 3)) {
            module = 'Modul 9';
            riskLevel = 'high';
          } else if (highSeverityCount >= 2 || moderateSeverityCount >= 4) {
            module = 'Modul 5';
            riskLevel = 'medium';
          }
          
          setReportData(prev => ({
            ...prev,
            recommendedModule: module,
            moduleRiskLevel: riskLevel
          }));
        } catch (error) {
          console.error('Error parsing assessment data:', error);
        }
      }
      // Don't redirect if no data - let user see the form anyway
    }
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Generate PDF or download document
    const content = generatePDFContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `modulskjema_${reportData.recommendedModule.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generatePDFContent = () => {
    return `MODULSKJEMA
=====================================

Innsattskjema for: ${reportData.inmateName || '_______________'}
Kontaktbetjent: ${reportData.contactPerson || '_______________'}

Helse kontaktet: Dato ________
Kontaktperson helse: ______________

VURDERINGSRESULTAT
-----------------
Vedtak+ celler: ${reportData.recommendedModule}
Modul dato: ${reportData.assessmentDate}
Modul vurdert av kontakt betj.: ${reportData.contactPerson || '_______________'}
Helse tilkalt dato: ________
Modul vurdert av helse: ________

=====================================
`;
  };

  const handleBack = () => {
    navigate('/assessment/module');
  };

  const handleNewAssessment = () => {
    sessionStorage.clear();
    navigate('/assessment/start');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 print:bg-white">
      {/* Header - Hidden in print */}
      <div className="print:hidden">
        <AssessmentLayout
          title="Modulskjema"
          subtitle="Utskriftsvennlig rapport for arkivering"
          backTo="/assessment/module"
          showBackButton={true}
          headerActions={
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewAssessment}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
              >
                <CheckCircle className="h-4 w-4" />
                Ny vurdering
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
              >
                <Printer className="h-4 w-4" />
                Skriv ut
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
              >
                <Download className="h-4 w-4" />
                Lagre
              </button>
            </div>
          }
        >

          {/* Printable Report - A4 size - Always light mode for printing */}
          <div 
            className="bg-white rounded-lg shadow-lg border border-gray-200 p-10 mx-auto print:shadow-none print:rounded-none print:p-0 print:border-0"
            style={{ 
              width: '210mm', 
              minHeight: '297mm',
              maxWidth: '100%'
            }}
          >
          <h1 className="text-2xl font-bold text-center mb-8 text-gray-900">
            Modulskjema
          </h1>
          
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Innsattskjema for:
              </label>
              <input
                type="text"
                value={reportData.inmateName}
                onChange={(e) => setReportData({...reportData, inmateName: e.target.value})}
                placeholder="Navn på innsatt"
                className="w-full px-3 py-2 border-b-2 border-gray-300 bg-white text-gray-900 focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kontaktbetjent:
              </label>
              <input
                type="text"
                value={reportData.contactPerson}
                onChange={(e) => setReportData({...reportData, contactPerson: e.target.value})}
                placeholder="Ditt navn og rolle"
                className="w-full px-3 py-2 border-b-2 border-gray-300 bg-white text-gray-900 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gjennomført av:
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {completedByName}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dato:
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {reportData.assessmentDate}
              </p>
            </div>
          </div>
          
          
          {/* Assessment Table */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4 text-gray-900">Vurderingsresultat</h2>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left text-sm text-gray-900">Vedtak+ celler</th>
                  <th className="border border-gray-300 px-4 py-2 text-sm text-gray-900">Modul dato</th>
                  <th className="border border-gray-300 px-4 py-2 text-sm text-gray-900">Modul vurdert av kontakt betj.</th>
                  <th className="border border-gray-300 px-4 py-2 text-sm text-gray-900">Helse tilkalt dato</th>
                  <th className="border border-gray-300 px-4 py-2 text-sm text-gray-900">Modul vurdert av helse</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3">
                    <span className={`font-bold ${
                      reportData.moduleRiskLevel === 'high' ? 'text-red-600' :
                      reportData.moduleRiskLevel === 'medium' ? 'text-yellow-600' :
                      'text-blue-600'
                    }`}>
                      {reportData.recommendedModule}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-900">
                    {reportData.assessmentDate}
                  </td>
                  <td className="border border-gray-300 px-4 py-3"></td>
                  <td className="border border-gray-300 px-4 py-3"></td>
                  <td className="border border-gray-300 px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          </div>
        </AssessmentLayout>
      </div>
    </div>
  );
} 